import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import i18n from 'i18n-js';

import ColorWrapper from '@styles/colors';
import Svg from '@styles/icons/svg';
import ModalWrapper from '@components/modal/ModalWrapper';
import { formatDateConditionally } from '@v2/helpers/time';
import { useCalculateModalPosition } from '@v2/pages/planboard/hooks/useCalculateModalPosition';
import usePriorityAdjustment from '@v2/pages/planboard/hooks/usePriorityAdjustment';
import usePriorityTooltipConfig from '@v2/pages/planboard/hooks/usePriorityTooltipConfig';
import ModalArrowPointer from '@v2/pages/planboard/components/JobCardPreview/ModalArrowPointer';
import JobCardInfoModalRow from './JobCardInfoModalRow';
import JobCardDaysBadge from '@v2/pages/planboard/components/JobCardPreview/JobCardDaysBadge';
import SimpleButton, { SimpleButtonVariant } from '@components/operatorPanel/components/elements/buttons/SimpleButton';
import { PriorityAdjustmentTooltipProps } from '@v2/pages/planboard/types';
import { PLANBOARD_TILE_CONFIG } from '@v2/pages/common/constants/base.constants';

const PriorityAdjustmentTooltip: React.FC<PriorityAdjustmentTooltipProps> = ({
  isOpen,
  onClose,
  item,
  anchorRef,
  onResetPriority,
  isResetting = false,
  C,
  scale,
  font,
}) => {
  const position = useCalculateModalPosition({ isOpen, anchorRef, scale });

  const {
    hasPriorityOverride,
    isPriorityIncreased,
    isPriorityDecreased,
    priorityDisplayText,
    daysDifference,
    isBehind,
    isAhead,
  } = usePriorityAdjustment({
    scheduledStartDate: item.scheduledStartDate,
    scheduledStartDateOverride: item.scheduledStartDateOverride,
    expectedStartOn: item.expectedStartOn,
    hasPriorityOverride: item.hasPriorityOverride,
  });

  const { priorityConfig, daysConfig } = usePriorityTooltipConfig({
    isPriorityIncreased,
    isPriorityDecreased,
    priorityDisplayText,
    daysDifference,
    isBehind,
    isAhead,
  });

  const modalWidth = scale(PLANBOARD_TILE_CONFIG.MODAL_WIDTH);

  return (
    <ModalWrapper isVisible={isOpen} C={C} onBackdropPress={onClose} backdropColor="transparent" backdropOpacity={0}>
      <ModalArrowPointer
        position={position}
        scale={scale}
        C={C}
        font={font}
        width={modalWidth}
        maxHeight={position.maxHeight}
        contentContainerStyle={{
          backgroundColor: C('Background/Light/Field on modal'),
        }}
      >
        <View style={{ padding: scale(16), paddingBottom: daysDifference !== null ? 0 : scale(16) }}>
          {hasPriorityOverride && priorityConfig && (
            <>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: scale(8),
                }}
              >
                <Text style={[font['Headline Large'], { color: C('Label/Light/Primary') }]}>
                  {priorityConfig.title}
                </Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(4) }}>
                  <View style={{ transform: [{ rotate: priorityConfig.arrowRotation }] }}>
                    <Svg icon="arrowVertical" width={scale(10)} height={scale(12)} color={priorityConfig.color} />
                  </View>
                  <Text style={[font['Headline Large'], { color: C(priorityConfig.color) }]}>
                    {priorityDisplayText}
                  </Text>
                </View>
              </View>

              <Text
                style={[
                  font['Subhead 1'],
                  { color: C('Label/Light/Tertiary'), marginBottom: scale(16) },
                ]}
              >
                {priorityConfig.description}
              </Text>

              <View style={{ alignItems: 'center', marginBottom: scale(16) }}>
                <SimpleButton
                  C={C}
                  scale={scale}
                  font={font}
                  variant={SimpleButtonVariant.Fill}
                  onPress={onResetPriority}
                  loading={isResetting}
                  style={{
                    height: scale(36),
                    paddingHorizontal: scale(16),
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(6) }}>
                    <Svg icon="refresh" width={scale(14)} height={scale(14)} color="Label/Light/AlwaysLight Primary" />
                    <Text style={[font['Button 1'], { color: C('Label/Light/AlwaysLight Primary') }]}>
                      {isResetting ? i18n.t('Resetting...') : priorityConfig.resetButtonText}
                    </Text>
                  </View>
                </SimpleButton>
              </View>

              <View
                style={{
                  height: StyleSheet.hairlineWidth,
                  backgroundColor: C('Hairline/Light/No trans [mobile]'),
                  marginBottom: scale(16),
                }}
              />
            </>
          )}

          <JobCardInfoModalRow
            label={i18n.t('Scheduled Start')}
            value={formatDateConditionally(item.scheduledStartDate) || '-'}
            isLast
            C={C}
            scale={scale}
            font={font}
          />
          <JobCardInfoModalRow
            label={i18n.t('Expected Start')}
            value={formatDateConditionally(item.expectedStartOn) || '-'}
            isLast
            C={C}
            scale={scale}
            font={font}
          />
        </View>

        {daysDifference !== null && daysConfig && (
          <View
            style={{
              backgroundColor: isBehind
                ? C('Background/Light/Planboard - Behind')
                : C('Background/Light/Planboard - Ahead'),
              borderBottomLeftRadius: scale(33),
              borderBottomRightRadius: scale(33),
              padding: scale(16),
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: scale(8),
              }}
            >
              <Text style={[font['Subhead 1'], { color: C('Label/Light/Primary') }]}>
                {daysConfig.label}
              </Text>
              <JobCardDaysBadge
                scheduledStartDate={item.scheduledStartDate}
                expectedStartOn={item.expectedStartOn}
                scale={scale}
                font={font}
                C={C}
              />
            </View>
            <Text style={[font['Footnote'], { color: C(daysConfig.color) }]}>
              {daysConfig.explanation}
            </Text>
          </View>
        )}
      </ModalArrowPointer>
    </ModalWrapper>
  );
};

export default ColorWrapper(PriorityAdjustmentTooltip);
